import {GameDataFirstGameState} from '@colonist/game-state-types'
import {GameStateEnums, NetworkEnums, PlayerEnums} from '../../../shared/GameEnums'
import {RoomState} from '../../../shared/RoomState'
import {SocketDataTypes} from '../../../shared/SocketDataTypes'
import {SocketActionController} from '../../shared/scripts/socket/SocketActionController'
import {UIUtilsEjsVariables} from '../../shared/scripts/UIUtilsEjsVariables'
import {SocketLobbySend} from '../../shared/scripts/socket/SocketLobbySend'
import {UIGameManager} from '../../shared/scripts/ui_game/UIGameManager'
import {UIManager} from './ui_html/UIManager'
import {Scene} from './ui_html/UISceneTypes'
import {UIUrlController} from '../../shared/scripts/ui_html/UIUrlController'
import {UIPopupFunnelIncentiveController} from './ui_popup/UIPopupFunnelIncentiveController'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {MatchmakingStatusUpdate} from '../../../shared/matchmaking/MatchmakingInterfaces'
import {UIAdBlockController} from './ui_ad/UIAdBlockController'
import {UIRoomController} from './ui_html/room/UIRoomController'
import {BaseError} from '../../../shared/errors/BaseError'
import {DiscordInstance} from './discord/DiscordInstance.ts'
import {ChatData} from '@colonist/shared-types'
import {UIDLandingPageController} from './ui_html/landing_page/UIDLandingPageController'
import RoomStateGotKickedData = NetworkEnums.RoomStateGotKickedData
import {GameEndData} from '@colonist/game-base-types'
import {UIWGeneralServicesController} from './UIWGeneralServicesController'
import {RoomPlayOrderSelectionData, RoomSeedOrderSelectionData} from '@colonist/lobby-types'
import {AnalyticEventValue} from '../../../shared/entity_datas/EntityDatas'
import {ViewPopupBecomeMemberGameplay} from '../../shared/scripts/ui_popup/ViewPopupBecomeMemberGameplay'
import {SDKRequest} from '../../../shared/DiscordActivityTypes'
import {ColonistMembershipType} from '@colonist/types'
import {navigateToPage} from '../../shared/scripts/ui_utils/ClientUtils.ts'
import {onSetPlayersJoinedRematch, setGlobalEndGameData} from '@colonist/ui-game'
import {StorageWrapper} from '../../shared/scripts/StorageWrapper.ts'

export class SocketWebActionController extends SocketActionController {
    readonly uiGameManager: UIGameManager | undefined
    private readonly uiManager: UIManager | undefined
    private hasDisplayedLoginIncentive: boolean = false

    constructor(uiGameManager: UIGameManager | undefined, uiManager: UIManager | undefined, generalServicesController: UIWGeneralServicesController) {
        super(generalServicesController)
        this.uiGameManager = uiGameManager
        this.uiManager = uiManager
    }

    onChatText(chatData: ChatData) {
        const isSceneRoom = this.uiManager?.sceneController.scene === Scene.Room
        if(isSceneRoom) {
            this.uiManager?.room.chat.post(chatData)
        }
    }

    onStateSessionState() {
        const isAdblockActive = this.uiManager?.adController.adblockState.isAdblockActive ?? false
        SocketLobbySend.setAdBlockStatusToUserSession(isAdblockActive)

        if(UIUtilsEjsVariables.isUserOnDiscord) {
            const discordInstance = DiscordInstance.getDiscordInstance()
            const voiceChannelId = discordInstance.voiceChannelId
            const serverProfile = discordInstance.serverProfile
            const roomId = UIUrlController.getRoomIdFromUrl()
            SocketLobbySend.joinChannel(voiceChannelId, serverProfile, discordInstance.isInitialLoad, roomId)
            UIPopupFunnelIncentiveController.showPopup(false)
            return
        }

        const accessGameFromLink = UIUrlController.getRoomIdFromUrl() !== undefined

        if(!accessGameFromLink) SocketLobbySend.autoRejoinGame()

        if(!this.hasDisplayedLoginIncentive) UIPopupFunnelIncentiveController.showPopup(accessGameFromLink)
        this.hasDisplayedLoginIncentive = true
    }

    onStateCustomRoom(data: RoomState) {
        this.uiManager?.room.updateRoom(data)
    }

    onSeedOrderSelection(data: RoomSeedOrderSelectionData) {
        this.uiManager?.room.updateSeedOrderSelection(data)
    }

    onPlayOrderSelection(data: RoomPlayOrderSelectionData) {
        this.uiManager?.room.updatePlayOrderSelection(data)
    }

    // eslint-disable-next-line class-methods-use-this
    onStateShowPremiumPopup() {
        const analyticEventValue = UIUtilsEjsVariables.isUserOnDiscord ? AnalyticEventValue.PopupMembershipIncentiveDiscordGeneral : AnalyticEventValue.RoomPremiumItemPopupBecomeAMemberButton
        const checkAction = UIUtilsEjsVariables.isUserOnDiscord ? () => {
            void DiscordInstance.getDiscordInstance().sendSDKRequest(SDKRequest.PurchaseMembership, ColonistMembershipType.PremiumMonthly)
        } : () => {
            navigateToPage('/store#membership', true)
        }
        new ViewPopupBecomeMemberGameplay(checkAction, analyticEventValue)
    }

    onStateDisplayInfoMessage(message: PlayerEnums.RoomInfoMessage) {
        this.uiManager?.room.center.infoMessageController.setMessage(message)
    }

    onStateGotKicked(data: RoomStateGotKickedData) {
        this.uiManager?.lobby.onGotKicked(data.roomId, data.canRejoin)
        this.uiManager?.room.leaveRoom()
    }

    onConnectionEstablished(data: GameDataFirstGameState) {
        this.uiManager?.sceneController.loadScene(Scene.Game)
        UIRoomController.updateURLToRoom(data.gameSettingId)
    }

    onStateInitialPlacement() {
        this.uiManager?.sceneController.loadScene(Scene.Game)
    }

    onGameEndState(gameEndData: GameEndData) {
        if(this.uiGameManager == undefined) return
        this.uiGameManager.gameState.isGameOver = true

        this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.GameEnd)
        this.uiGameManager.toolTipController.closeTip()
        this.uiGameManager.settingsController.onGameEnded()

        setGlobalEndGameData(gameEndData)

        return undefined
    }

    onMatchmakingUpdate(matchmakingStatusUpdate: MatchmakingStatusUpdate) {
        this.uiManager?.matchmakingController.updateMatchmakingStatus(matchmakingStatusUpdate)
    }

    onMatchmakingError(error: BaseError) {
        this.uiManager?.matchmakingController.showMatchmakingErrorPopup(error)
    }

    onMatchmakingShowPreRollAd() {
        this.uiManager?.vliController?.startMatchmakingVideoAd()
    }

    // eslint-disable-next-line class-methods-use-this
    onMatchmakingDisableAdblock() {
        const noContinueAction = () => undefined
        UIAdBlockController.showDisableAdblockPopup(noContinueAction, false, false)
    }

    onChannelRoomUpdate(roomData: SocketDataTypes.ChannelRoomUpdate) {
        if(!UIUtilsEjsVariables.isUserOnDiscord) return
        if(!(this.uiManager?.landingPage instanceof UIDLandingPageController)) return
        this.uiManager.landingPage.updateDiscordCTAContent(roomData)
        this.uiManager.mainPage.ctaController.updateDiscordCTAContent(roomData)
        DiscordInstance.getDiscordInstance().loadingScreen.hideBackground()
    }

    onRoomListUpdate(data: NetworkEnums.RoomListUpdateData) {
        this.uiManager?.lobby.updateRoomList(data)
    }

    // eslint-disable-next-line class-methods-use-this
    onBeginnerModeLevelEnd() {
        StorageWrapper.incrementBeginnerModeLevel()
    }

    // eslint-disable-next-line class-methods-use-this
    onEndGameText(data: any) {
        const playersJoinedRematch: number = data
        onSetPlayersJoinedRematch(playersJoinedRematch)
    }
}
